import ProposalApplicationStatusEnum from '@constants/enums/proposal/applicationStatus';
import ProposalFundingStatusEnum from '@constants/enums/proposal/fundingStatus';
import ProposalStatusEnum from '@constants/enums/proposal/status';

import i18n from '@plugins/i18n';

import { Collection, Proposal } from '@/models';

export class ProposalCollection extends Collection {
    static type = 'ProposalCollection';
    static endpoint = Proposal.api.index;
    static model = Proposal;

    static fields() {
        return {
            ...super.fields(),
            investor_id: this.string(null).nullable()
        };
    }

    get preset_filters() {
        let filters = {};

        if (this.investor_id) {
            filters.investor_id = this.filterOperator('is') + ':' + this.investor_id;
        }

        return filters;
    }

    get headers() {
        return [
            this.createHeader('client_name', i18n.t('client'), {
                filterable: true,
                path: 'client.name',
                width: '10rem',
                mock: '{{person.fullName}}'
            }),
            this.createHeader('offer_id', i18n.tc('offer'), {
                width: '18rem',
                classList: ['no-wrap'],
                sortable: 'offer.name',
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/Offer.vue'),
                    filters: {
                        off_panel: true,
                        closed: true
                    }
                },
                path: 'offer.name',
                mock: '{{company.name}}'
            }),
            this.createHeader('proposed_at', i18n.t('proposed_at'), {
                filterable: true,
                format: 'datetime',
                width: '12rem'
            }),
            this.createHeader('status', i18n.t('status'), {
                filterable: true,
                width: '10rem',
                format: 'enum',
                enum: ProposalStatusEnum,
                enumA11yKey: 'enums.proposal_status',
                // eslint-disable-next-line max-len
                mock: `{{helpers.arrayElement(["${ProposalStatusEnum.PROPOSED}","${ProposalStatusEnum.IN_PROGRESS}","${ProposalStatusEnum.AWAITING_APPROVAL}","${ProposalStatusEnum.AWAITING_CONFIRMATION}"])}}`
            }),
            this.createHeader('amount', i18n.t('amount'), {
                filterable: true,
                format: 'money',
                width: '12rem',
                formatOptions: {
                    decimals: 2,
                    abbreviate: false
                }
            }),
            this.createHeader('funding_status', i18n.t('funding_status'), {
                filterable: true,
                width: '11rem',
                align: 'right',
                format: 'enum',
                enum: ProposalFundingStatusEnum,
                enumA11yKey: 'enums.proposal_funding_status',
                enumTypes: {
                    [ProposalFundingStatusEnum.AWAITING_FUNDS]: 'grey',
                    [ProposalFundingStatusEnum.FUNDS_AVAILABLE]: 'warning',
                    [ProposalFundingStatusEnum.FUNDS_TRANSFERRED]: 'success',
                    [ProposalFundingStatusEnum.FUNDS_CLEARED]: 'success'
                }
            }),
            this.createHeader('application_status', i18n.t('application_status'), {
                filterable: true,
                width: '11rem',
                align: 'right',
                format: 'enum',
                enum: ProposalApplicationStatusEnum,
                enumA11yKey: 'enums.proposal_application_status',
                enumTypes: {
                    [ProposalApplicationStatusEnum.PENDING]: 'grey',
                    [ProposalApplicationStatusEnum.SENT]: 'info',
                    [ProposalApplicationStatusEnum.SIGNED]: 'success',
                    [ProposalApplicationStatusEnum.EXPIRED]: 'error',
                    [ProposalApplicationStatusEnum.ABORTED]: 'error'
                }
            }),
            this.createHeader('bespoke_provider_fees', i18n.t('bespoke_provider_fees'), {
                filterable: true,
                width: '8rem',
                align: 'right',
                format: 'boolean'
            }),
            this.createHeader('control', '', {
                format: 'control',
                width: '11rem',
                persistent: true
            })
        ];
    }

    get default_headers() {
        return [
            'client_name',
            'offer_id',
            'proposed_at',
            'status',
            'amount',
            'funding_status',
            'application_status',
            'bespoke_provider_fees'
        ];
    }
}

export default ProposalCollection;
